// Login, registration, email verification and password reset (04 §4.2).
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import { AuthLayout } from '../components/AuthLayout';
import { Button, ErrorNotice, Notice, PasswordField, Spinner, TextField } from '../components/ui';
import { useSession } from '../state/session';
import { useLogout } from '../state/useLogout';

/** Only same-app paths, so a crafted link can't redirect elsewhere. */
export const safeNext = (next: string | null): string =>
  next?.startsWith('/') && !next.startsWith('//') ? next : '/galaxies';

const linkClass = 'text-info hover:text-info';

export function LoginScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signIn = useSession((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => endpoints.login({ email, password }),
    onSuccess: (data) => {
      signIn(data.accessToken, data.account);
      navigate(safeNext(params.get('next')), { replace: true });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout title="Log in">
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {login.error ? <ErrorNotice error={login.error} /> : null}
        <Button type="submit" className="w-full" disabled={login.isPending}>
          Log in
        </Button>
      </form>
      <div className="flex justify-between text-sm">
        <Link className={linkClass} to="/register">
          Create account
        </Link>
        <Link className={linkClass} to="/forgot-password">
          Forgot password?
        </Link>
      </div>
      <p className="text-center text-sm text-muted">
        New here?{' '}
        <Link className={linkClass} to="/welcome">
          What is Stars &amp; Ships?
        </Link>
      </p>
    </AuthLayout>
  );
}

/** After registering (15a leftover, P23): the address the confirmation mail went to. */
export function CheckEmailScreen() {
  const { logout } = useLogout();
  const navigate = useNavigate();
  const account = useSession((s) => s.account);
  const resend = useMutation({ mutationFn: endpoints.resendVerification });
  if (account?.emailVerified) return <Navigate to="/galaxies" replace />;
  return (
    <AuthLayout title="Check your email">
      <p className="text-sm text-muted">
        We sent a confirmation link to{' '}
        <span className="font-medium text-text">{account?.email}</span>. Open it to confirm your
        account, then join a galaxy.
      </p>
      <Button
        variant="secondary"
        className="w-full"
        disabled={resend.isPending || resend.isSuccess}
        onClick={() => resend.mutate()}
      >
        {resend.isSuccess ? 'Sent' : 'Send again'}
      </Button>
      <p className="text-xs text-muted">
        Wrong address?{' '}
        <button type="button" className="text-info underline" onClick={logout}>
          Log out
        </button>{' '}
        and register again.
      </p>
      <Button className="w-full" onClick={() => navigate('/galaxies')}>
        Continue
      </Button>
    </AuthLayout>
  );
}

/** The "Repeat password" field: the mismatch shows once the repeat is long enough or on submit. */
function useRepeatPassword(password: string) {
  const [repeat, setRepeat] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const mismatch = repeat !== password;
  const show = submitted || (repeat !== '' && repeat.length >= password.length);
  return {
    field: {
      value: repeat,
      onChange: (e: ChangeEvent<HTMLInputElement>) => setRepeat(e.target.value),
      error: show && mismatch ? 'The passwords do not match.' : undefined,
    },
    /** Marks a submit attempt; true when both passwords match. */
    confirm: () => {
      setSubmitted(true);
      return !mismatch;
    },
  };
}

export function RegisterScreen() {
  const signIn = useSession((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const repeat = useRepeatPassword(password);
  const [inviteCode, setInviteCode] = useState('');
  const info = useQuery({ queryKey: ['registration-info'], queryFn: endpoints.registrationInfo });
  // While the answer is unknown the field stays visible: a server without a code ignores it.
  const needsCode = info.data?.inviteCodeRequired ?? info.isError;
  const register = useMutation({
    mutationFn: () => endpoints.register({ email, password, ...(needsCode ? { inviteCode } : {}) }),
    // Signing in makes the /register route redirect to /check-email (App.tsx).
    onSuccess: (data) => signIn(data.accessToken, data.account),
  });

  return (
    <AuthLayout title="Create your account">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (repeat.confirm()) register.mutate();
        }}
        className="space-y-4"
      >
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          autoComplete="new-password"
          required
          minLength={10}
          hint="At least 10 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label="Repeat password"
          autoComplete="new-password"
          required
          {...repeat.field}
        />
        {needsCode ? (
          <TextField
            label="Invite code"
            autoComplete="off"
            required
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            hint="You got it with your invitation."
          />
        ) : null}
        {register.error ? <ErrorNotice error={register.error} /> : null}
        <Button type="submit" className="w-full" disabled={register.isPending || info.isPending}>
          Create account
        </Button>
      </form>
      <p className="text-sm text-muted">
        Already playing?{' '}
        <Link className={linkClass} to="/login">
          Log in
        </Link>
      </p>
    </AuthLayout>
  );
}

export function VerifyEmailScreen() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const status = useSession((s) => s.status);
  const setAccount = useSession((s) => s.setAccount);
  const started = useRef(false);
  const verify = useMutation({
    mutationFn: () => endpoints.verifyEmail(token),
    onSuccess: async () => {
      if (useSession.getState().status === 'authenticated') {
        setAccount((await endpoints.me()).account);
      }
    },
  });

  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    verify.mutate();
  }, [token, verify]);

  return (
    <AuthLayout title="Confirm email">
      {!token ? (
        <Notice tone="error">This link is incomplete. Open the link from the email again.</Notice>
      ) : null}
      {verify.isPending ? <Spinner label="Confirming…" /> : null}
      {verify.error ? <ErrorNotice error={verify.error} /> : null}
      {verify.isSuccess ? (
        <>
          <Notice tone="success">Your email is confirmed.</Notice>
          <Link to={status === 'authenticated' ? '/galaxies' : '/login'} className={linkClass}>
            Continue
          </Link>
        </>
      ) : null}
    </AuthLayout>
  );
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const forgot = useMutation({ mutationFn: () => endpoints.forgotPassword(email) });
  return (
    <AuthLayout title="Reset password">
      {forgot.isSuccess ? (
        <Notice tone="success">
          If an account exists for this email, we sent a link to reset the password.
        </Notice>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            forgot.mutate();
          }}
          className="space-y-4"
        >
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {forgot.error ? <ErrorNotice error={forgot.error} /> : null}
          <Button type="submit" className="w-full" disabled={forgot.isPending}>
            Send reset link
          </Button>
        </form>
      )}
      <Link className={`text-sm ${linkClass}`} to="/login">
        Back to login
      </Link>
    </AuthLayout>
  );
}

export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const repeat = useRepeatPassword(password);
  const reset = useMutation({ mutationFn: () => endpoints.resetPassword(token, password) });
  return (
    <AuthLayout title="Choose a new password">
      {reset.isSuccess ? (
        <>
          <Notice tone="success">Your password was changed. Other devices were logged out.</Notice>
          <Link className={linkClass} to="/login">
            Log in
          </Link>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (repeat.confirm()) reset.mutate();
          }}
          className="space-y-4"
        >
          <PasswordField
            label="New password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordField
            label="Repeat new password"
            autoComplete="new-password"
            required
            {...repeat.field}
          />
          {reset.error ? <ErrorNotice error={reset.error} /> : null}
          <Button type="submit" className="w-full" disabled={reset.isPending || !token}>
            Change password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
